import {BaseUnitType, Term} from '@benchling/units';
import {css, StyleSheet} from 'aphrodite';
import cn from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BLUE_POWDER} from 'ui-library/Colors';
import TextField from 'ui-library/inputs/TextField';

import FormulaCanvas from './FormulaCanvas';
import UnitTypeTermsDisplay from './UnitTypeTermsDisplay';
import {HydratedUnit} from './util';

interface UnitTypeManualCreationScreenProps {
  name: string;
  onNameChange: (name: string) => void;
  simplifiedTerms: Array<Term<BaseUnitType>>;
  numeratorUnits: Array<HydratedUnit>;
  onSetNumeratorUnits: (fn: (units: Array<HydratedUnit>) => Array<HydratedUnit>) => void;
  denominatorUnits: Array<HydratedUnit>;
  onSetDenominatorUnits: (fn: (units: Array<HydratedUnit>) => Array<HydratedUnit>) => void;
  baseUnitName: string;
  onSetBaseUnitName: (name: string) => void;
  baseUnitSymbol: string;
  onSetBaseUnitSymbol: (symbol: string) => void;
  errorMessage: string;
}

export default function UnitTypeManualCreationScreen({
  name,
  onNameChange,
  simplifiedTerms,
  numeratorUnits,
  onSetNumeratorUnits,
  denominatorUnits,
  onSetDenominatorUnits,
  baseUnitName,
  onSetBaseUnitName,
  baseUnitSymbol,
  onSetBaseUnitSymbol,
  errorMessage,
}: UnitTypeManualCreationScreenProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'UnitTypeManualCreationScreen', element: 'root'})}>
      <div className={css(styles.topRow)}>
        <TextField
          label="Unit type name"
          isRequired={true}
          className={cn('u-spacing-bottom', css(styles.nameInput))}
          value={name}
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          dataTestAttrs={{component: 'UnitTypeManualCreationScreen', element: 'unitTypeName'}}
        />
        <div className={css(styles.dimensionsalFormulaSection)}>
          <b>Dimensional Formula</b>
          {simplifiedTerms.length > 0 && <UnitTypeTermsDisplay terms={simplifiedTerms} />}
        </div>
      </div>
      <label className="fieldLabel--label">Formula</label>
      <FormulaCanvas
        numeratorUnits={numeratorUnits}
        denominatorUnits={denominatorUnits}
        setNumeratorUnits={onSetNumeratorUnits}
        setDenominatorUnits={onSetDenominatorUnits}
      />
      <div className="u-flex-display">
        <TextField
          label="Base unit name"
          isRequired={true}
          className={cn('u-spacing-top', 'u-spacing-right', css(styles.nameInput))}
          value={baseUnitName}
          onChange={(e) => {
            onSetBaseUnitName(e.target.value);
          }}
          dataTestAttrs={{component: 'UnitTypeManualCreationScreen', element: 'baseUnitName'}}
        />
        <TextField
          label="Base unit symbol"
          isRequired={true}
          className={cn('u-spacing-top', css(styles.symbolInput))}
          value={baseUnitSymbol}
          onChange={(e) => {
            onSetBaseUnitSymbol(e.target.value);
          }}
          dataTestAttrs={{component: 'UnitTypeManualCreationScreen', element: 'baseUnitSymbol'}}
        />
      </div>
      {errorMessage.length > 0 && (
        <div className="u-spacing-top">
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  nameInput: {
    width: '200px',
  },
  symbolInput: {
    width: '120px',
  },
  dimensionsalFormulaSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '300px',
    height: '90px',
    backgroundColor: BLUE_POWDER,
    margin: '10px',
    padding: '10px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});
